import { Button, Card, CardBody, useDisclosure } from "@nextui-org/react";
import React, { useState } from "react";
import DeleteModal from "../../../components/DeleteModal";
import { deleteOrg } from "../../../controllers/APIController";

/**
 * DangerZoneOrg Component
 * Critical organization management section for deleting the entire organization
 * Provides organization deletion functionality with confirmation and error handling
 * @returns {JSX.Element} Organization danger zone interface
 */
const DangerZoneOrg = () => {
  // Modal disclosure hook for delete confirmation
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onOpenChange: onDeleteOpenChange,
  } = useDisclosure();

  // Component state
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);

  // User and organization data from session storage
  const userLogged = JSON.parse(sessionStorage.getItem("userData"));
  const orgInfo = userLogged && userLogged.user.LastOrgId;
  
  // Debug environment variable
  console.log("Middleware URL:", process.env.REACT_APP_MIDDLEWARE_IP_ADDRESS);

  /**
   * Handle organization deletion
   * Deletes the organization and clears user session
   * Includes comprehensive error handling and logging
   */
  const handleDeleteOrganization = async () => {
    console.log("DeleteHandler called with OrgInfo:", orgInfo);
    console.log("UserLogged data:", userLogged);
    
    if (!orgInfo) {
      setError("Organization ID not found. Please try logging out and logging back in.");
      return;
    }

    setIsDeleting(true);
    setError(null);
    
    try {
      console.log("Making delete org API call with payload:", { orgId: orgInfo });
      const response = await deleteOrg({ orgId: orgInfo });
      console.log("Organization deleted successfully:", response);
      
      // Clear user session and redirect to login
      sessionStorage.clear();
      localStorage.clear();
      window.location.href = "/signin";
    } catch (error) {
      console.error("Error deleting organization:", error);
      setError(`Failed to delete organization: ${error.message}. Please try again or contact support.`);
      setIsDeleting(false);
    }
  };

  /**
   * Debug function to display environment and user information
   * Helps troubleshoot organization deletion issues
   */
  const handleDebugInfo = () => {
    console.log("Environment check:");
    console.log("REACT_APP_MIDDLEWARE_IP_ADDRESS:", process.env.REACT_APP_MIDDLEWARE_IP_ADDRESS);
    console.log("UserLogged:", userLogged);
    console.log("OrgInfo:", orgInfo);
  };

  return (
    <>
      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={isDeleteOpen}
        onOpenChange={onDeleteOpenChange}
        DeleteHandler={handleDeleteOrganization}
        Text="Organization"
      />
      
      {/* Warning Description */}
      <div className="my-8">
        This section allows you to manage and delete your organization. Please be cautious when performing this action, as it will permanently remove all associated data and access rights.
        
        {/* Error Display */}
        {error && (
          <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}
      </div>
      
      {/* Danger Zone Card */}
      <div className="flex items-center">
        <Card className="shadow-none border border-red-400 rounded-2xl p-4 w-full my-4">
          <CardBody>
            <div className="flex items-center justify-between">
              <p className="text-lg font-normal">Delete your Organization</p>
              
              {/* Action Buttons */}
              <div className="flex gap-2">
                {/* Debug Button */}
                <Button
                  size="sm"
                  onClick={handleDebugInfo}
                  className="text-sm"
                  color="secondary"
                >
                  Debug Info
                </Button>
                
                {/* Delete Button */}
                <Button
                  size="sm"
                  onClick={onDeleteOpen}
                  className="text-sm"
                  color="danger"
                  isDisabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Delete Organization"}
                </Button>
              </div>
            </div>

            {/* Warning Text */}
            <p className="text-sm pt-2">
              This will permanently remove the organization you will lose access
              to all your data inside your space.
            </p>
          </CardBody>
        </Card>
      </div>
    </>
  );
};

export default DangerZoneOrg;
